import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { AIService } from 'src/app/ai/ai.service';
import { ComanageService } from 'src/app/comanage/comanage.service';
import { CompanyState } from 'src/app/companies/company.state';
import { FetchContacts } from 'src/app/contacts/contacts.actions';
import { ContactState } from 'src/app/contacts/contacts.state';
import { FetchCustomers as FetchAppCustomers } from 'src/app/customers/customers.actions';
import { CustomerState } from 'src/app/customers/customers.state';
import { FetchEmployees } from 'src/app/employees/employees.actions';
import { EmployeeState } from 'src/app/employees/employees.state';
import { AssignedResume } from 'src/app/models/AssignedResume';
import { ContactPerson } from 'src/app/models/ContactPerson';
import Customer from 'src/app/models/Customer';
import { Employee } from 'src/app/models/Employee';
import { GlobalTag } from 'src/app/models/GlobalTag';
import { JiraProjectCredential } from 'src/app/models/JiraProjectCredential';
import { LinkedFileToObject } from 'src/app/models/LinkedFileToObject';
import { ModuleEnum } from 'src/app/models/ModuleEnum';
import { Partner } from 'src/app/models/Partner';
import { Product } from 'src/app/models/Product';
import Project from 'src/app/models/Project';
import { Resume } from 'src/app/models/resume/Resume';
import { PartnerState } from 'src/app/partners/partners.state';
import { FetchResumes } from 'src/app/resumes/resume.actions';
import { ResumeState } from 'src/app/resumes/resume.state';
import { ModuleService } from 'src/app/services/module.service';
import { CountryService } from 'src/app/shared/country.service';
import { GlobalFilesService } from 'src/app/shared/global-files/global-files.service';
import { JiraLoginService } from 'src/app/shared/login-components/jira-login/jira-login.service';

import { FetchPartners, GetPartner } from '../../partners/partners.actions';
import { ProjectAccountModalComponent } from '../project-account-modal/project-account-modal.component';
import { ProjectAddProductModalComponent } from '../project-add-product-modal/project-add-product-modal.component';
import { ProjectDeleteProductComponent } from '../project-delete-product/project-delete-product.component';
import { AddProject, AssignResumeToProject, DeleteAssignedResume, FetchProjects, UpdateProject } from '../project.actions';
import { ProjectService } from '../project.service';
import { ProjectState } from '../project.state';

export interface ProjectFormTab {
    id: string;
    label: string;
    controls: string[];
}

/** Constant data, so it lives beside the component rather than being rebuilt per instance. */
export const PROJECT_FORM_TABS: ProjectFormTab[] = [
    {
        id: 'general',
        label: 'General',
        controls: [
            'title',
            'projectType',
            'jiraProjectKey',
            'description',
            'billable',
            'present',
            'startDate',
            'endDate',
            'warranty',
            'warrantyStartDate',
            'warrantyEndDate',
            'maintenanceContract',
        ],
    },
    { id: 'location', label: 'Location', controls: ['location'] },
    {
        id: 'linked',
        label: 'Linked entities',
        controls: ['partner', 'customer', 'comanageCustomer', 'contact', 'businessDeveloperUuid'],
    },
    {
        id: 'invoicing',
        label: 'Invoicing',
        controls: [
            'invoiceMethod',
            'invoiceByDay',
            'hoursPerDay',
            'poRequired',
            'sesRequired',
            'paymentTermDays',
            'researchDevelopmentEligible',
            'researchDevelopmentPercentage',
        ],
    },
    {
        id: 'timeRegistration',
        label: 'Time registration',
        controls: ['timesheetRequired', 'timesheetType', 'signedTimesheetRequired'],
    },
];

@Component({
    selector: 'app-project-add-modal',
    templateUrl: './project-detail-modal.component.html',
    styleUrls: ['./project-detail-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ProjectDetailModalComponent implements OnInit, OnChanges, OnDestroy {
    public emitObject: EventEmitter<Project> = new EventEmitter();
    @Input() project: Project;
    @Input() inOverview = false;
    @Input() partnerUuidInput: string;
    @Input() fromDeal: boolean;
    @Select(PartnerState.getPartners) partners$: Observable<Partner[]>;
    @Select(CompanyState.companyUuid) companyUuid$: Observable<string>;
    @Select(CustomerState.Customers) customers$: Observable<Customer[]>;
    @Select(ContactState.Contacts) contacts$: Observable<ContactPerson[]>;
    @Select(EmployeeState.Employees) employees$: Observable<Employee[]>;
    @Select(ResumeState.resumes) companyResumes$: Observable<Resume[]>;
    @Select(ProjectState.project) project$: Observable<Project>;
    allPartners: Partner[];
    allCustomers: Customer[];
    allContacts: ContactPerson[];
    allEmployees: Employee[] = [];
    newProject: Project;
    projectFormGroup: UntypedFormGroup;
    errorMessage: string;
    connectionError: string;
    present = false;
    billable = false;
    isUpdate = false;
    loading = false;
    warranty = false;
    isHiddenControl = true;
    maintenanceContract = false;
    public inputIsWrong = false;
    /**
     * The form is long enough that as one scrolling column it was hard to find anything, so it
     * is split across tabs. Every section stays in the DOM and is only hidden, rather than being
     * destroyed and rebuilt the way ngbNav would do it: the controls are reactive and would
     * survive that, but the autocomplete and tag components inside them re-initialise, and a
     * field the user never opened must not be able to lose its value on the way to save.
     *
     * The control names are listed so an invalid field can be found on a tab the user is not
     * looking at. Without that, submitting a form whose only problem is three tabs away looks
     * like a button that does nothing.
     */
    tabs: ProjectFormTab[] = PROJECT_FORM_TABS;

    activeTab = 'general';

    selectTab(id: string) {
        this.activeTab = id;
    }

    /** Marks a tab in the nav so a problem the user has scrolled past is still findable. */
    tabHasErrors(tabId: string): boolean {
        if (!this.projectFormGroup) {
            return false;
        }
        const tab = this.tabs.find((candidate) => candidate.id === tabId);
        if (!tab) {
            return false;
        }
        // The date range checks live outside the form group, so ask for them separately.
        if (tabId === 'general' && (!this.isValidDate || !this.isValidWarrantyDate || this.descriptionIsTooLong)) {
            return true;
        }
        return tab.controls.some((name) => {
            const control = this.projectFormGroup.get(name);
            return !!control && control.invalid;
        });
    }

    public projectWentWrong = false;
    isValidDate = false;
    isValidWarrantyDate = false;
    error: any = { isError: false, errorMessage: '' };
    tags: GlobalTag[] = [];
    DESCRIPTION_MAX_LENGTH = 1000;
    currentAmountOfDescriptionCharacters: number;
    descriptionIsTooLong = false;
    companyUuid: string;
    allCountries = [];
    selectedResumes: Resume[] = [];
    assignedResumes: AssignedResume[];
    resumesToRemove: AssignedResume[] = [];
    checkModule: any;
    isLoaded: boolean;
    companyResumes: Resume[];
    partner: Partner;
    isFinished: boolean;
    currentPartner: Partner;
    currentCustomer: Customer;
    currentContact: ContactPerson;
    eModule = ModuleEnum;
    jiraProjectKeyList$: JiraProjectCredential[] = [];
    jiraProjectOptions: Array<JiraProjectCredential & { searchLabel: string }> = [];
    jiraProjectKeysLoaded = false;
    coManageCustomers: any[] = [];
    coManageCustomerOptions: any[] = [];
    isCoManageAddressLocked = false;
    aiExtractingProjectSettings = false;
    projectDocuments: LinkedFileToObject[] = [];
    selectedProjectDocumentId: string = null;
    // Used on edit when clicked on edit in overview
    projectFullyLoaded = true;
    keyword = 'firstName';
    partnerKeyword = 'title';
    private subscriptions = new Subscription();

    constructor(
        public activeModal: NgbActiveModal,
        private modalService: NgbModal,
        public moduleService: ModuleService,
        private countryService: CountryService,
        private toastr: ToastrService,
        private store: Store,
        private formBuilder: UntypedFormBuilder,
        private logger: NGXLogger,
        private projectService: ProjectService,
        private cdRef: ChangeDetectorRef,
        private jiraLoginService: JiraLoginService,
        private aiService: AIService,
        private globalFilesService: GlobalFilesService,
        private comanageService: ComanageService,
    ) {
        this.checkModule = this.moduleService.checkModuleActive(this.eModule.RESUME);
    }

    refreshProjectAccounts() {
        if (!this.project || !this.project.uuid) return;
        this.projectService.getProjectAccounts(this.project.uuid).subscribe(
            (accounts) => {
                this.project = {
                    ...(this.project as any),
                    projectAccounts: accounts || [],
                } as Project;
            },
            (err) => {
                this.logger.warn('Failed to load project accounts', err);
            },
        );
    }

    @ViewChild('focussed', { static: false }) set input(element: ElementRef<HTMLInputElement>) {
        if (element) {
            element.nativeElement.focus();
        }
    }

    ngOnInit(): void {
        console.log('🏃 ProjectDetailModal ngOnInit: Starting');

        if (this.project && !this.fromDeal) {
            this.isUpdate = true;
        }

        if (this.project?.uuid) {
            this.loadProjectDocuments();
        }

        // eslint-disable-next-line @typescript-eslint/ban-types
        this.jiraLoginService.getJiraProjectKeys().subscribe((data: Object[]) => {
            for (const obj of data) {
                const name = obj['projectName'] ? obj['projectName'] : '';
                const key = obj['projectKey'] ? obj['projectKey'] : '';
                const id = obj['projectId'] ? obj['projectId'] : '';
                const jiraProjectCredential = new JiraProjectCredential(name, key, id);
                this.jiraProjectKeyList$.push(jiraProjectCredential);
            }
            this.jiraProjectOptions = this.jiraProjectKeyList$.map((credential) => ({
                ...credential,
                searchLabel: `${credential.projectName || ''} - ${credential.jiraKey || ''}`.trim(),
            }));
            this.jiraProjectKeysLoaded = true;
            // After keys are loaded, preselect the alpha key on edit
            this.patchJiraSelectionIfAvailable();
        });

        // this.jiraLoginService.getJiraProjectKeys().subscribe((projectKeys) => {
        //     this. jiraProjectKeys$ = projectKeys as string[];
        //     this.jiraProjectKeysLoaded = true;
        // });

        this.createProjectForm();
        console.log('🧵 ProjectDetailModal: Calling fetchData()');
        this.fetchData();

        if (this.project) {
            this.tags = this.project.globalTags;
            if (this.project.warranty) {
                this.warranty = true;
            }
            if (this.project.maintenanceContract) {
                this.maintenanceContract = true;
            }
        }

        if (this.partnerUuidInput) {
            this.logger.debug('Fetching partner');
            this.subscriptions.add(
                this.store.dispatch(new GetPartner(this.partnerUuidInput)).subscribe((state) => {
                    this.currentPartner = state.partner.partners.find((partner) => partner.uuid == this.partnerUuidInput);
                }),
            );
        }

        this.allCountries = this.countryService.getAllCountries();
        this.isFinished = this.project?.finished ? this.project.finished : false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.project?.currentValue?.uuid) {
            this.loadProjectDocuments();
        }
    }

    setFormValues() {
        if (!this.projectFullyLoaded) return;
        console.log('setting values');
        if (this.project.present) {
            this.present = this.project?.present;
            this.isValidDate = true;

            this.projectFormGroup.patchValue({
                // Preselect Jira key by mapping stored numeric id/key to the alphanumeric key
                jiraProjectKey: this.resolveJiraOptionFromProject(),
                startDate: moment(this.project.startDate).format('YYYY-MM-DD'),
                endDate: moment(Date.now()).format('YYYY-MM-DD'),
                present: this.project.present,
                billable: this.project.billable,
                warranty: this.project.warranty,
                title: this.project.title,
                projectType: this.project.projectType ? this.getType(this.project.projectType.toString()) : this.getType(''),
                description: this.project.description,
                location: this.project?.location,
                pdfUpload: this.project.pdfUpload,
                partner: this.partner ?? null,
                customer: this.project.customerUuid ? this.currentCustomer : null,
                comanageCustomer: this.getCoManageCustomerById(this.project.comanageContactId ?? ''),
                contact: this.project.contactPersonUuid ? this.currentContact : null,
                businessDeveloperUuid: this.project.businessDeveloperUuid ?? null,
                budget: this.project.budget,
                budgetInManDays: this.project.budgetInManDays,
                timesheetRequired: this.project.timesheetRequired ?? false,
                timesheetType: this.project.timesheetType ?? '',
                signedTimesheetRequired: this.project.signedTimesheetRequired ?? false,
                poRequired: this.project.poRequired ?? false,
                sesRequired: this.project.sesRequired ?? false,
                paymentTermDays: this.project.paymentTermDays ?? 30,
                invoiceByDay: this.project.invoiceByDay ?? false,
                invoiceMethod: this.project.invoiceMethod ?? this.getDefaultInvoiceMethod(this.project.projectType),
                hoursPerDay: this.project.hoursPerDay ?? null,
                researchDevelopmentEligible: this.project.researchDevelopmentEligible ?? false,
                researchDevelopmentPercentage: this.getResearchDevelopmentPercentageForEdit(),
                warrantyStartDate: this.project.warrantyStartDate
                    ? moment(this.project.warrantyStartDate).format('YYYY-MM-DD')
                    : moment(Date.now()).format('YYYY-MM-DD'),
                warrantyEndDate: this.project.warrantyEndDate
                    ? moment(this.project.warrantyEndDate).format('YYYY-MM-DD')
                    : moment().add(4, 'months').format('YYYY-MM-DD'),
                maintenanceContract: this.project.maintenanceContract,
            });
            this.projectFormGroup.controls.endDate.disable();
        } else {
            this.projectFormGroup.patchValue({
                // Preselect Jira key by mapping stored numeric id/key to the alphanumeric key
                jiraProjectKey: this.resolveJiraOptionFromProject(),
                startDate: moment(this.project.startDate).format('YYYY-MM-DD'),
                endDate: this.project.endDate ? moment(this.project.endDate).format('YYYY-MM-DD') : null,
                present: this.project.present,
                warranty: this.project.warranty,
                billable: this.project.billable,
                title: this.project.title,
                description: this.project.description,
                location: this.project?.location,
                pdfUpload: this.project.pdfUpload,
                partner: this.partner ?? null,
                customer: this.project.customerUuid ? this.currentCustomer : null,
                comanageCustomer: this.getCoManageCustomerById(this.project.comanageContactId ?? ''),
                contact: this.project.contactPersonUuid ? this.currentContact : null,
                businessDeveloperUuid: this.project.businessDeveloperUuid ?? null,
                budget: this.project.budget,
                budgetInManDays: this.project.budgetInManDays,
                projectType: this.project.projectType ? this.getType(this.project.projectType.toString()) : this.getType(''),
                timesheetRequired: this.project.timesheetRequired ?? false,
                timesheetType: this.project.timesheetType ?? '',
                signedTimesheetRequired: this.project.signedTimesheetRequired ?? false,
                poRequired: this.project.poRequired ?? false,
                sesRequired: this.project.sesRequired ?? false,
                paymentTermDays: this.project.paymentTermDays ?? 30,
                invoiceByDay: this.project.invoiceByDay ?? false,
                invoiceMethod: this.project.invoiceMethod ?? this.getDefaultInvoiceMethod(this.project.projectType),
                hoursPerDay: this.project.hoursPerDay ?? null,
                researchDevelopmentEligible: this.project.researchDevelopmentEligible ?? false,
                researchDevelopmentPercentage: this.getResearchDevelopmentPercentageForEdit(),
                warrantyStartDate: this.project.warrantyStartDate
                    ? moment(this.project.warrantyStartDate).format('YYYY-MM-DD')
                    : moment(Date.now()).format('YYYY-MM-DD'),
                warrantyEndDate: this.project.warrantyEndDate
                    ? moment(this.project.warrantyEndDate).format('YYYY-MM-DD')
                    : moment().add(4, 'months').format('YYYY-MM-DD'),
                maintenanceContract: this.project.maintenanceContract,
            });
        }
    }

    private resolveJiraKeyFromProject(): string {
        // Backend stores jiraProjectKey as a numeric string; view models also expose jiraProjectId.
        // We map either value to the alphanumeric key for the select.
        if (!this.jiraProjectKeyList$ || !this.jiraProjectKeyList$.length) return '';
        const idFromModel = (this.project as any)?.jiraProjectId;
        let numericId: number | null = null;
        if (typeof idFromModel === 'number' && idFromModel > 0) {
            numericId = idFromModel;
        } else if (this.project?.jiraProjectKey) {
            const parsed = Number((this.project.jiraProjectKey || '').toString());
            numericId = isNaN(parsed) ? null : parsed;
        }
        if (numericId && numericId > 0) {
            const found = this.jiraProjectKeyList$.find((c) => Number(c.projectId) === numericId);
            return found?.jiraKey || '';
        }
        return '';
    }

    private patchJiraSelectionIfAvailable() {
        // Only patch when editing and keys list is available
        if (!this.projectFormGroup || !this.jiraProjectOptions?.length) return;
        const selected = this.resolveJiraOptionFromProject();
        if (selected) {
            this.projectFormGroup.patchValue({ jiraProjectKey: selected });
            this.projectFormGroup.get('jiraProjectKey')?.updateValueAndValidity({ emitEvent: false });
        }
    }

    private resolveJiraOptionFromProject(): (JiraProjectCredential & { searchLabel: string }) | null {
        const key = this.resolveJiraKeyFromProject();
        if (!key) {
            return null;
        }
        return this.jiraProjectOptions.find((option) => option.jiraKey === key) || null;
    }

    onPresentTicked() {
        this.present = this.projectFormGroup.controls.present.value;
        if (this.present) {
            this.isValidDate = true;
            this.projectFormGroup.controls.endDate.disable();
        } else {
            this.isValidDate = false;
            this.projectFormGroup.controls.endDate.enable();
        }
    }

    onWarrantyTicked() {
        if (this.warranty) {
            this.projectFormGroup.controls.warrantyStartDate.disable();
            this.projectFormGroup.controls.warrantyEndDate.disable();
        } else {
            this.projectFormGroup.controls.warrantyStartDate.enable();
            this.projectFormGroup.controls.warrantyEndDate.enable();
        }
        this.warranty = !this.warranty;
        this.isHiddenControl = !this.isHiddenControl;
        this.cdRef.detectChanges(); // Trigger change detection
    }

    onMaintenanceTicked() {
        this.maintenanceContract = !this.maintenanceContract;
    }

    onDescriptionKeyUp() {
        this.descriptionIsTooLong = false;
        this.currentAmountOfDescriptionCharacters = this.projectFormGroup.controls.description.value.length;
        if (this.currentAmountOfDescriptionCharacters > this.DESCRIPTION_MAX_LENGTH) {
            this.descriptionIsTooLong = true;
        }
    }

    private revealFirstTabWithErrors() {
        const firstBadTab = this.tabs.find((tab) => this.tabHasErrors(tab.id));
        if (firstBadTab) {
            this.activeTab = firstBadTab.id;
        }
    }

    saveProject() {
        this.loading = true;

        if (!this.present) {
            this.isValidDate = this.validateDates(this.projectFormGroup.value.startDate, this.projectFormGroup.value.endDate);
        }

        if (this.warranty) {
            this.isValidWarrantyDate = this.validateWarrantyDates(this.projectFormGroup.value.warrantyStartDate, this.projectFormGroup.value.warrantyEndDate);
        }

        if (!this.warranty) {
            this.isValidWarrantyDate = true;
        }

        if (this.descriptionIsTooLong) {
            this.loading = false;
            this.revealFirstTabWithErrors();
            return;
        }

        if (!this.projectFormGroup.valid || !this.isValidDate || !this.isValidWarrantyDate) {
            this.inputIsWrong = true;
            this.loading = false;
            // Touch everything first: a control the user never visited is pristine, so its field
            // would not show as invalid even once we put them on the right tab.
            this.projectFormGroup.markAllAsTouched();
            this.revealFirstTabWithErrors();
            return;
        }

        // Read raw values so disabled controls (e.g., endDate, warranty dates) are included
        const formValue = this.projectFormGroup.getRawValue();
        this.projectFormGroup.disable();

        this.newProject = formValue;
        this.newProject.companyUuid = this.companyUuid;
        // Ensure timesheet fields are present in payload
        this.newProject.timesheetRequired = !!formValue.timesheetRequired;
        this.newProject.timesheetType = (formValue.timesheetType ?? '').trim();
        this.newProject.signedTimesheetRequired = !!formValue.signedTimesheetRequired && this.newProject.timesheetRequired && !!this.newProject.timesheetType;
        this.newProject.poRequired = !!formValue.poRequired;
        this.newProject.sesRequired = !!formValue.sesRequired;
        this.newProject.paymentTermDays = this.normalizePaymentTermDays(formValue.paymentTermDays);
        this.newProject.invoiceByDay = !!formValue.invoiceByDay;
        this.newProject.invoiceMethod = formValue.invoiceMethod || this.getDefaultInvoiceMethod(formValue.projectType);
        this.newProject.researchDevelopmentEligible = !!formValue.researchDevelopmentEligible;
        this.newProject.researchDevelopmentPercentage = this.newProject.researchDevelopmentEligible
            ? this.normalizeResearchDevelopmentPercentage(formValue.researchDevelopmentPercentage)
            : 0;
        this.newProject.comanageContactId = this.getCoManageCustomerId(formValue.comanageCustomer) || null;
        this.newProject.businessDeveloperUuid = formValue.businessDeveloperUuid || null;
        // Persist hours-per-day (used for AI checks only)
        this.newProject.hoursPerDay = formValue.hoursPerDay ?? null;

        // Debug log outgoing payload subset
        this.logger.debug('Saving project with timesheet config', {
            uuid: this.project?.uuid,
            timesheetRequired: this.newProject.timesheetRequired,
            timesheetType: this.newProject.timesheetType,
            signedTimesheetRequired: this.newProject.signedTimesheetRequired,
        });

        if (!this.newProject.warranty) {
            this.newProject.warrantyStartDate = null;
            this.newProject.warrantyEndDate = null;
        }

        if (this.fromDeal) {
            this.newProject.products = this.project.products;
        }

        const partnerVal = formValue.partner;
        if (partnerVal && partnerVal.uuid) {
            this.newProject.partnerUuid = partnerVal.uuid;
        }

        if (formValue.customer?.uuid) {
            this.newProject.customerUuid = formValue.customer.uuid;
            this.newProject.customerFirstname = formValue.customer.firstName;
            this.newProject.customerLastname = formValue.customer.lastName;
        } else {
            this.newProject.customerUuid = null;
            this.newProject.customerFirstname = null;
            this.newProject.customerLastname = null;
        }

        if (formValue.contact?.uuid) {
            this.newProject.contactPersonUuid = formValue.contact.uuid;
            this.newProject.contactPersonFirstname = formValue.contact.firstName;
            this.newProject.contactPersonLastname = formValue.contact.lastName;
        } else {
            this.newProject.contactPersonUuid = null;
            this.newProject.contactPersonFirstname = null;
            this.newProject.contactPersonLastname = null;
        }

        // Map selected Jira alphanumeric key to numeric project ID and send as jiraProjectKey (int)
        const jiraControlDirty = this.projectFormGroup.get('jiraProjectKey')?.dirty ?? false;
        if (formValue.jiraProjectKey) {
            const selectedKey = typeof formValue.jiraProjectKey === 'string' ? formValue.jiraProjectKey : formValue.jiraProjectKey?.jiraKey;
            const selected = this.jiraProjectKeyList$.find((c) => c.jiraKey === selectedKey);
            const projectId = selected?.projectId ? Number(selected.projectId) : null;
            if (projectId && projectId > 0) {
                // Backend DTO expects 'jiraProjectKey' as an integer (ID)
                (this.newProject as any).jiraProjectKey = projectId;
            } else {
                // No valid selection: do not include the field
                delete (this.newProject as any).jiraProjectKey;
            }
            // Ensure we don't send an inconsistent string value
            delete (this.newProject as any).jiraProjectId;
        } else if (this.isUpdate && jiraControlDirty) {
            // Explicitly cleared by user in edit mode
            (this.newProject as any).jiraProjectKey = 0;
            delete (this.newProject as any).jiraProjectId;
        } else {
            // No selection: remove both jira fields
            delete (this.newProject as any).jiraProjectKey;
            delete (this.newProject as any).jiraProjectId;
        }

        if (!this.isUpdate) {
            this.logger.debug('Attempt to add project');
            this.subscriptions.add(
                this.store.dispatch(new AddProject(this.newProject)).subscribe({
                    next: (state) => {
                        const createdProject = state.project.projects.find((w) => this.newProject.title === w.title);
                        this.emitObject.emit(createdProject);
                        if (this.moduleService.checkModuleActive(this.eModule.RESUME)) {
                            this.logger.debug('Attempt to assign resume to project');
                            this.store.dispatch(new AssignResumeToProject(state.project.currentProject.uuid, this.selectedResumes)).subscribe(
                                () => {
                                    this.logger.debug('Succesfully assigned resume to project');
                                },
                                () => {
                                    this.logger.error('Failed to assign resume to project');
                                },
                            );
                        }
                        this.store.dispatch(new FetchProjects());
                        this.logger.debug('Succesfully added project');
                        this.toastr.success('project added succesfully');
                        this.activeModal.close();
                        this.tags = [];
                    },
                    error: (error) => {
                        this.logger.error('Failed to add project');
                        this.errorMessage = error?.error?.message ?? 'Error message was empty';

                        this.loading = false;
                        this.projectFormGroup.enable();

                        if (!this.errorMessage) {
                            this.connectionError = 'No connection to the server';
                        }

                        this.projectWentWrong = true;
                    },
                }),
            );
        } else {
            this.newProject.finished = this.isFinished;
            this.logger.debug('Attempt to update project');
            this.subscriptions.add(
                this.store.dispatch(new UpdateProject(this.project.uuid, this.newProject)).subscribe({
                    next: () => {
                        if (this.moduleService.checkModuleActive(this.eModule.RESUME)) {
                            this.resumesToRemove.forEach((element) => {
                                this.logger.debug('Attempt to delete assigned resume');
                                this.store.dispatch(new DeleteAssignedResume(element.projectResumeAssignmentUuid)).subscribe(
                                    () => {
                                        this.logger.debug('Succesfully deleted assigned resume');
                                    },
                                    () => {
                                        this.logger.debug('Failed to delete assigned resume');
                                    },
                                );
                            });

                            this.logger.debug('Attempt to assign resume to project');
                            this.store.dispatch(new AssignResumeToProject(this.project?.uuid, this.selectedResumes)).subscribe(
                                () => {
                                    this.logger.debug('Succesfully assigned resume to project');
                                },
                                () => {
                                    this.logger.error('Failed to assign resume to project');
                                },
                            );
                        }

                        this.logger.debug('Fetching project');
                        this.store.dispatch(new FetchProjects());

                        this.activeModal.close();
                    },
                    error: (error) => {
                        this.logger.error('Failed to update project');
                        this.errorMessage = error?.error?.message ?? 'Error message was empty';

                        this.loading = false;
                        this.projectFormGroup.enable();

                        if (!this.errorMessage) {
                            this.connectionError = 'No connection to the server';
                        }

                        this.projectWentWrong = true;
                    },
                }),
            );
        }
    }

    hideErrorMessage() {
        this.inputIsWrong = false;
    }

    onCoManageCustomerChange(rawValue: string) {
        this.applyCoManageCustomerSelection(rawValue);
    }

    validateDates(sDate: Date, eDate: Date) {
        this.isValidDate = true;
        if (sDate == null || eDate == null) {
            this.error = { isError: true, errorMessage: 'Start date and end date are required.' };
            this.isValidDate = false;
        }

        if (sDate != null && eDate != null && eDate < sDate) {
            this.error = { isError: true, errorMessage: 'End date should be greater then start date.' };
            this.isValidDate = false;
        }

        return this.isValidDate;
    }

    validateWarrantyDates(warrantyStartDate: Date, warrantyEndDate: Date) {
        if (warrantyStartDate == null || warrantyEndDate == null) {
            this.error = { isError: true, errorMessage: 'Start and end date are required.' };
        }

        if (warrantyStartDate != null && warrantyEndDate != null && warrantyEndDate < warrantyStartDate) {
            this.error = { isError: true, errorMessage: 'End date should be greater then start date.' };
        }

        if (warrantyStartDate != null && warrantyEndDate != null && warrantyStartDate < warrantyEndDate) {
            this.isValidWarrantyDate = true;
        }

        return this.isValidWarrantyDate;
    }

    selectEvent(item) {
        this.selectedResumes.push(item);
        this.companyResumes.splice(this.companyResumes.indexOf(item), 1);
        this.companyResumes = [...this.companyResumes];
    }

    addToRemoveArray(res: AssignedResume) {
        if (!this.resumesToRemove.includes(res)) {
            this.resumesToRemove.push(res);
        }
        if (this.assignedResumes.includes(res)) {
            this.assignedResumes.splice(this.assignedResumes.indexOf(res), 1);
        }
    }

    removeFromSelectedArray(res: Resume) {
        this.companyResumes.push(res);
        this.companyResumes = [...this.companyResumes];
        this.selectedResumes.splice(this.selectedResumes.indexOf(res), 1);
    }

    checkResumeModule() {
        return this.moduleService.checkModuleActive(this.eModule.RESUME);
    }

    toggleFinished() {
        this.isFinished = !this.isFinished;
    }

    openProjectAddProductModal(project: Project, product?: Product) {
        this.logger.debug('Opening projectAddProductModal');
        const modalRef = this.modalService.open(ProjectAddProductModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.project = project;
        modalRef.componentInstance.product = product;
    }

    openProjectProductDeleteModal(project: Project, product: Product) {
        this.logger.debug('Opening projectDeleteProductModal');
        const modalRef = this.modalService.open(ProjectDeleteProductComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.product = product;
        modalRef.componentInstance.project = project;
    }

    getRelevantContacts() {
        const partner = this.projectFormGroup.get('partner').value;
        if (this.allContacts) {
            return partner ? this.allContacts.filter((c) => c.partnerUuid === partner.uuid) : this.allContacts;
        } else {
            return partner;
        }
    }

    employeeName(employee: Employee): string {
        return `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || employee?.uuid || '';
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private createProjectForm() {
        const uuid = this.isUpdate ? this.project.uuid : '0';

        this.projectFormGroup = this.formBuilder.group({
            title: ['', [Validators.required]],
            projectType: ['', [Validators.required]],
            jiraProjectKey: [null],
            description: [''],
            pdfUpload: null,
            billable: true,
            present: false,
            startDate: ['', [Validators.required]],
            endDate: ['', [Validators.required]],
            warranty: false,
            warrantyStartDate: ['', [Validators.required]],
            warrantyEndDate: ['', [Validators.required]],
            maintenanceContract: false,
            invoiceByDay: false,
            invoiceMethod: ['BY_ACCOUNT'],
            hoursPerDay: [null],
            paymentTermDays: [30, [Validators.min(1), Validators.max(180)]],
            timesheetRequired: false,
            timesheetType: [''],
            signedTimesheetRequired: false,
            poRequired: false,
            sesRequired: false,
            researchDevelopmentEligible: false,
            researchDevelopmentPercentage: [100, [Validators.min(0), Validators.max(100)]],
            partner: [null],
            customer: [null],
            comanageCustomer: [null],
            contact: [null],
            businessDeveloperUuid: [null],
            location: this.formBuilder.group({
                street: [''],
                number: [null],
                bus: [''],
                ext: [null],
                postal: [''],
                city: [''],
                country: this.formBuilder.group({
                    iso2: [''],
                }),
            }),
            budget: [null],
            budgetInManDays: [null],
        });
        this.projectFormGroup.controls.warrantyStartDate.disable();
        this.projectFormGroup.controls.warrantyEndDate.disable();

        // Set up conditional validation for timesheets
        this.setupTimesheetValidators();
        this.setupInvoiceMethodDefaults();
        this.setupResearchDevelopmentValidators();
        this.setupCoManageAddressBehavior();
    }

    private setupResearchDevelopmentValidators() {
        const eligibleControl = this.projectFormGroup.get('researchDevelopmentEligible');
        const percentageControl = this.projectFormGroup.get('researchDevelopmentPercentage');
        if (!eligibleControl || !percentageControl) return;

        this.subscriptions.add(
            eligibleControl.valueChanges.subscribe((eligible) => {
                if (eligible) {
                    percentageControl.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
                    const currentPercentage = Number(percentageControl.value);
                    if (
                        percentageControl.value === null ||
                        percentageControl.value === undefined ||
                        percentageControl.value === '' ||
                        currentPercentage === 0
                    ) {
                        percentageControl.setValue(100, { emitEvent: false });
                    }
                } else {
                    percentageControl.setValidators([Validators.min(0), Validators.max(100)]);
                }
                percentageControl.updateValueAndValidity({ emitEvent: false });
            }),
        );
    }

    private getResearchDevelopmentPercentageForEdit(): number {
        const percentage = Number(this.project?.researchDevelopmentPercentage);
        if (!Number.isFinite(percentage) || percentage <= 0) {
            return 100;
        }
        return Math.min(100, percentage);
    }

    private normalizeResearchDevelopmentPercentage(value: any): number {
        const parsed = Number(value);
        if (value === null || value === undefined || value === '' || isNaN(parsed)) {
            return 100;
        }
        return Math.min(100, Math.max(0, parsed));
    }

    private setupCoManageAddressBehavior() {
        const comanageContactCtrl = this.projectFormGroup.get('comanageCustomer');
        if (!comanageContactCtrl) return;

        this.subscriptions.add(
            comanageContactCtrl.valueChanges.subscribe((value) => {
                this.applyCoManageCustomerSelection(value);
            }),
        );
    }

    private applyCoManageCustomerSelection(selectedValue: any) {
        const selectedCustomer =
            selectedValue && typeof selectedValue === 'object' ? selectedValue : this.getCoManageCustomerById((selectedValue ?? '').toString().trim());

        if (!selectedCustomer) {
            this.isCoManageAddressLocked = false;
            this.setLocationControlsDisabled(false);
            return;
        }

        const locationPatch = this.extractLocationFromCoManageCustomer(selectedCustomer);
        this.projectFormGroup.patchValue({ location: locationPatch }, { emitEvent: false });
        this.isCoManageAddressLocked = true;
        this.setLocationControlsDisabled(true);
    }

    private setLocationControlsDisabled(disabled: boolean) {
        const locationGroup = this.projectFormGroup.get('location');
        if (!locationGroup) return;

        if (disabled) {
            locationGroup.disable({ emitEvent: false });
        } else {
            locationGroup.enable({ emitEvent: false });
        }
    }

    private getCoManageCustomerById(id: string): any | null {
        if (!id) return null;
        return (
            (this.coManageCustomerOptions || []).find((customer) => {
                const possibleIds = [customer?.comanageOptionId, customer?.number, customer?.supplier_number, customer?.customer_number, customer?.id]
                    .filter((value) => value !== null && value !== undefined && `${value}`.trim() !== '')
                    .map((value) => String(value).trim());
                return possibleIds.includes(id);
            }) || null
        );
    }

    getCoManageCustomerId(customer: any): string {
        const raw = customer?.comanageOptionId ?? customer?.number ?? customer?.supplier_number ?? customer?.customer_number ?? customer?.id ?? '';
        return raw != null ? String(raw).trim() : '';
    }

    getCoManageCustomerLabel(customer: any): string {
        const name = (customer?.name || '').toString().trim();
        const id = this.getCoManageCustomerId(customer);
        return id ? `${name || 'Unnamed customer'} (${id})` : name || 'Unnamed customer';
    }

    private extractLocationFromCoManageCustomer(customer: any) {
        const preferredAddress = this.getPreferredCoManageAddress(customer);

        const street =
            this.getNestedValue(preferredAddress, [
                'street',
                'street_name',
                'streetname',
                'address_line_1',
                'line1',
                'line_1',
                'address1',
                'address_1',
                'address',
            ]) ||
            this.getNestedValue(customer, ['street', 'street_name', 'streetname', 'address.street', 'invoice_address.street', 'address_line_1', 'line1']) ||
            '';
        const numberRaw =
            this.getNestedValue(preferredAddress, ['number', 'street_number', 'house_number', 'houseNumber', 'housenumber']) ||
            this.getNestedValue(customer, ['number', 'house_number', 'houseNumber', 'address.number', 'address.house_number']);

        const bus =
            this.getNestedValue(preferredAddress, ['bus', 'box', 'suite', 'unit', 'apartment']) ||
            this.getNestedValue(customer, ['bus', 'box', 'address.bus', 'address.box']) ||
            '';

        const extRaw = this.getNestedValue(preferredAddress, ['ext', 'extension']) || this.getNestedValue(customer, ['ext', 'extension', 'address.ext']);

        const postal =
            this.getNestedValue(preferredAddress, ['postal', 'postal_code', 'postcode', 'zipcode', 'zip', 'zip_code']) ||
            this.getNestedValue(customer, [
                'postal',
                'postal_code',
                'zipcode',
                'zip',
                'zip_code',
                'address.postal',
                'address.postal_code',
                'address.zipcode',
                'address.zip',
                'address.zip_code',
                'invoice_address.postal',
                'invoice_address.postal_code',
                'invoice_address.zipcode',
                'invoice_address.zip',
            ]) ||
            '';

        const city =
            this.getNestedValue(preferredAddress, ['city', 'town', 'locality', 'municipality']) ||
            this.getNestedValue(customer, ['city', 'town', 'address.city', 'invoice_address.city']) ||
            '';

        const countryRaw =
            this.getNestedValue(preferredAddress, ['country', 'country_code', 'country_iso2', 'iso2', 'alpha2', 'alpha_2']) ||
            this.getNestedValue(customer, [
                'country',
                'country_code',
                'country_iso2',
                'address.country',
                'address.country_code',
                'address.country_iso2',
                'invoice_address.country',
                'invoice_address.country_code',
                'invoice_address.country_iso2',
            ]) ||
            '';

        return {
            street,
            number: this.toNullableNumber(numberRaw),
            bus,
            ext: this.toNullableNumber(extRaw),
            postal,
            city,
            country: {
                iso2: this.resolveCountryIso2(countryRaw),
            },
        };
    }

    private getPreferredCoManageAddress(customer: any): any | null {
        const directCandidates = [customer?.invoice_address, customer?.billing_address, customer?.address, customer?.primary_address, customer?.location]
            .map((candidate) => this.unwrapAddressCandidate(candidate))
            .filter((candidate) => !!candidate);

        if (directCandidates.length > 0) {
            return directCandidates[0];
        }

        const addressCollections = [
            customer?.addresses,
            customer?.contact_addresses,
            customer?.customer_addresses,
            customer?.data?.addresses,
            customer?.attributes?.addresses,
        ];

        for (const collection of addressCollections) {
            const addresses = this.toAddressArray(collection)
                .map((candidate) => this.unwrapAddressCandidate(candidate))
                .filter((candidate) => !!candidate);

            if (!addresses.length) continue;

            const prioritized =
                addresses.find((address) => this.addressMatchesKind(address, ['invoice', 'billing'])) ||
                addresses.find((address) => this.addressMatchesKind(address, ['default', 'primary'])) ||
                addresses[0];

            if (prioritized) {
                return prioritized;
            }
        }

        return null;
    }

    private toAddressArray(value: any): any[] {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.data)) return value.data;
        return [];
    }

    private unwrapAddressCandidate(candidate: any): any | null {
        if (!candidate || typeof candidate !== 'object') return null;

        if (candidate?.address && typeof candidate.address === 'object') {
            return {
                ...candidate,
                ...candidate.address,
            };
        }

        if (candidate?.attributes && typeof candidate.attributes === 'object') {
            return {
                ...candidate,
                ...candidate.attributes,
            };
        }

        return candidate;
    }

    private addressMatchesKind(address: any, kinds: string[]): boolean {
        const rawType = address?.type ?? address?.kind ?? address?.address_type ?? address?.label ?? address?.name ?? '';

        const normalized = rawType.toString().toLowerCase();
        return kinds.some((kind) => normalized.includes(kind));
    }

    private getNestedValue(object: any, paths: string[]): any {
        for (const path of paths) {
            const value = path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), object);
            if (value !== undefined && value !== null && `${value}`.trim() !== '') {
                return value;
            }
        }
        return null;
    }

    private toNullableNumber(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private resolveCountryIso2(value: any): string {
        const countryCandidate =
            typeof value === 'object' && value !== null ? value.code ?? value.iso2 ?? value.alpha2 ?? value['alpha-2'] ?? value.name ?? '' : value;

        const normalized = (countryCandidate ?? '').toString().trim();
        if (!normalized) return '';

        if (normalized.length === 2) {
            return normalized.toUpperCase();
        }

        const lower = normalized.toLowerCase();
        const match = (this.allCountries || []).find((country) => (country?.name || '').toString().toLowerCase() === lower);
        return match ? (match['alpha-2'] || '').toString().toUpperCase() : '';
    }

    private setupTimesheetValidators() {
        const requiredCtrl = this.projectFormGroup.get('timesheetRequired');
        const typeCtrl = this.projectFormGroup.get('timesheetType');
        const signedCtrl = this.projectFormGroup.get('signedTimesheetRequired');
        const invoiceCtrl = this.projectFormGroup.get('invoiceByDay');
        const hoursCtrl = this.projectFormGroup.get('hoursPerDay');
        if (!requiredCtrl || !typeCtrl) return;

        const applyValidators = (isRequired: boolean) => {
            if (isRequired) {
                typeCtrl.setValidators([Validators.required]);
            } else {
                typeCtrl.clearValidators();
            }
            typeCtrl.updateValueAndValidity({ emitEvent: false });
        };

        // Apply initial state
        applyValidators(!!requiredCtrl.value);
        // React to changes
        this.subscriptions.add(requiredCtrl.valueChanges.subscribe((val) => applyValidators(!!val)));

        const normalizeSignedRequirement = () => {
            if (!signedCtrl) return;
            const hasTimesheetType = !!(typeCtrl.value || '').toString().trim();
            const canRequireSigned = !!requiredCtrl.value && hasTimesheetType;
            if (!canRequireSigned) {
                signedCtrl.setValue(false, { emitEvent: false });
            }
        };

        normalizeSignedRequirement();
        this.subscriptions.add(requiredCtrl.valueChanges.subscribe(() => normalizeSignedRequirement()));
        this.subscriptions.add(typeCtrl.valueChanges.subscribe(() => normalizeSignedRequirement()));

        // Apply hours-per-day validators only when invoiceByDay is enabled
        const applyHoursValidators = (byDay: boolean) => {
            if (!hoursCtrl) return;
            if (byDay) {
                hoursCtrl.setValidators([Validators.min(1), Validators.max(12)]);
            } else {
                hoursCtrl.clearValidators();
            }
            hoursCtrl.updateValueAndValidity({ emitEvent: false });
        };
        applyHoursValidators(!!invoiceCtrl?.value);
        if (invoiceCtrl) {
            this.subscriptions.add(invoiceCtrl.valueChanges.subscribe((val) => applyHoursValidators(!!val)));
        }
    }

    private setupInvoiceMethodDefaults() {
        const typeCtrl = this.projectFormGroup.get('projectType');
        const methodCtrl = this.projectFormGroup.get('invoiceMethod');
        if (!typeCtrl || !methodCtrl) return;

        if (!methodCtrl.value) {
            methodCtrl.setValue(this.getDefaultInvoiceMethod(typeCtrl.value), { emitEvent: false });
        }

        this.subscriptions.add(
            typeCtrl.valueChanges.subscribe((projectType) => {
                if (!this.isUpdate) {
                    methodCtrl.setValue(this.getDefaultInvoiceMethod(projectType), { emitEvent: false });
                }
            }),
        );
    }

    getDefaultInvoiceMethod(projectType: any): 'BY_TIME' | 'BY_ACCOUNT' {
        const value = (projectType ?? '').toString().toUpperCase();
        return value === '2' || value === 'CONSULTANCY' ? 'BY_TIME' : 'BY_ACCOUNT';
    }

    private normalizePaymentTermDays(value: any): number {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) {
            return 30;
        }
        return Math.min(180, Math.max(1, Math.trunc(parsed)));
    }

    async extractProjectSettingsWithAi(): Promise<void> {
        try {
            this.aiExtractingProjectSettings = true;
            if (!this.selectedProjectDocumentId) {
                this.toastr.info('Select a document from Files first.');
                return;
            }

            const uploadedDocument = await firstValueFrom(this.globalFilesService.fetchUploadedDocumentById(this.selectedProjectDocumentId));
            const sourceUrl = uploadedDocument?.url || null;
            if (!sourceUrl) {
                this.toastr.error('Selected Files document has no URL available.');
                return;
            }

            const result = await this.aiService.suggestProjectInvoicingTimeFromPdfUrl(sourceUrl);

            this.projectFormGroup.patchValue({
                invoiceByDay: result.invoiceByDay ?? this.projectFormGroup.get('invoiceByDay')?.value ?? false,
                poRequired: result.poRequired ?? this.projectFormGroup.get('poRequired')?.value ?? false,
                paymentTermDays: result.paymentTermDays ?? this.projectFormGroup.get('paymentTermDays')?.value ?? 30,
                timesheetRequired: result.timesheetRequired ?? this.projectFormGroup.get('timesheetRequired')?.value ?? false,
                timesheetType: result.timesheetType || this.projectFormGroup.get('timesheetType')?.value || '',
                hoursPerDay: result.hoursPerDay ?? this.projectFormGroup.get('hoursPerDay')?.value ?? null,
            });

            if (
                result.invoiceByDay === null &&
                result.poRequired === null &&
                result.paymentTermDays === null &&
                result.timesheetRequired === null &&
                !result.timesheetType &&
                result.hoursPerDay === null
            ) {
                this.toastr.info('AI found no invoicing/time registration settings in this document.');
            } else {
                this.toastr.success('Invoicing and time registration fields extracted.');
            }
        } catch (err: any) {
            this.logger.error('AI extraction for project settings failed', err);
            this.toastr.error(err?.message || 'AI extraction failed.');
        } finally {
            this.aiExtractingProjectSettings = false;
        }
    }

    private loadProjectDocuments(): void {
        if (!this.project?.uuid) {
            this.projectDocuments = [];
            this.selectedProjectDocumentId = null;
            return;
        }

        this.projectDocuments = [];
        this.selectedProjectDocumentId = null;

        this.subscriptions.add(
            this.globalFilesService.getAllFilesLinkedToObject(this.project.uuid).subscribe(
                (linked) => {
                    const linkedFiles = Array.isArray(linked?.linkedFiles) ? linked.linkedFiles : [];
                    this.projectDocuments = linkedFiles
                        .filter((file) => (file?.fileType || '').toLowerCase() === 'uploadeddocument')
                        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                    this.ensureDefaultSelectedDocument();
                },
                (err) => {
                    this.logger.warn('Failed loading linked Files-tab documents for project', err);
                    this.projectDocuments = [];
                    this.ensureDefaultSelectedDocument();
                },
            ),
        );
    }

    refreshProjectDocuments(): void {
        this.loadProjectDocuments();
    }

    getDocumentDisplayName(document: LinkedFileToObject): string {
        const raw = String(document?.title || '').trim();
        if (!raw) return 'Unnamed document';
        const normalized = raw.replace(/\\/g, '/');
        const parts = normalized.split('/').filter((part) => !!part);
        return parts.length ? parts[parts.length - 1] : raw;
    }

    private ensureDefaultSelectedDocument(): void {
        if (!this.selectedProjectDocumentId && this.projectDocuments.length) {
            this.selectedProjectDocumentId = this.projectDocuments[0].fileId;
        }
    }

    private fetchData() {
        if (this.isUpdate) {
            this.projectFullyLoaded = false;
            this.warranty = this.project.warranty;
            this.maintenanceContract = this.project.maintenanceContract;

            if (this.warranty) {
                this.isHiddenControl = false;
                this.projectFormGroup.controls.warrantyStartDate.enable();
                this.projectFormGroup.controls.warrantyEndDate.enable();
            }

            this.setFormValues();
            this.projectFullyLoaded = true;

            // Load project accounts for display
            this.refreshProjectAccounts();

            //ToDo: Check if this code can be deleted permanently!!!
            // this.subscriptions.add(
            //     this.project$.subscribe((project) => {
            //         if (!project || project.uuid !== this.project.uuid) {
            //             this.store.dispatch(new FetchProjectById(this.project.uuid));
            //             return;
            //         }

            //         this.projectFullyLoaded = true;
            //         if (!this.fromDeal) this.project = project;
            //         this.setFormValues();
            //     }),
            // );
        }

        if (!this.isUpdate) {
            this.projectFormGroup.patchValue({
                warrantyStartDate: moment(Date.now()).format('YYYY-MM-DD'),
                warrantyEndDate: moment().add(4, 'months').format('YYYY-MM-DD'),
            });
        }

        this.subscriptions.add(this.companyResumes$.subscribe((resumes) => (this.companyResumes = resumes)));

        this.subscriptions.add(
            this.customers$.subscribe((customers) => {
                if (!customers) {
                    this.logger.debug('Fetching customers');
                    this.store.dispatch(new FetchAppCustomers());
                } else {
                    if (this.project && this.project.customerUuid) {
                        this.currentCustomer = JSON.parse(JSON.stringify(customers.find((c) => c.uuid == this.project.customerUuid)));
                    }
                    this.allCustomers = customers;
                }
            }),
        );

        if (this.moduleService.checkModuleActive(this.eModule.COMANAGE)) {
            this.subscriptions.add(
                this.comanageService.getContacts().subscribe(
                    (contacts: any[]) => {
                        this.coManageCustomers = [...(contacts || [])].sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || ''));
                        this.coManageCustomerOptions = this.coManageCustomers.map((customer: any) => {
                            const optionId = this.getCoManageCustomerId(customer);
                            return {
                                ...customer,
                                comanageOptionId: optionId,
                                searchLabel: this.getCoManageCustomerLabel(customer),
                            };
                        });

                        const selected = this.project?.comanageContactId ? this.getCoManageCustomerById(this.project.comanageContactId) : null;
                        if (selected) {
                            this.applyCoManageCustomerSelection(selected);
                            this.projectFormGroup?.patchValue({ comanageCustomer: selected }, { emitEvent: false });
                        }
                    },
                    (err) => {
                        this.logger.warn('Failed to load CoManage contacts for project modal', err);
                        this.coManageCustomers = [];
                    },
                ),
            );
        }

        this.subscriptions.add(
            this.contacts$.subscribe((contacts) => {
                if (!contacts) {
                    this.logger.debug('Fetching contacts');
                    this.store.dispatch(new FetchContacts());
                } else {
                    if (this.project && this.project.contactPersonUuid) {
                        this.currentContact = JSON.parse(JSON.stringify(contacts.find((c) => c.uuid === this.project.contactPersonUuid)));
                    }
                    this.allContacts = contacts;
                }
            }),
        );

        this.subscriptions.add(
            this.employees$.subscribe((employees) => {
                if (!employees) {
                    this.logger.debug('Fetching employees');
                    this.store.dispatch(new FetchEmployees());
                } else {
                    this.allEmployees = [...employees].sort((a, b) => this.employeeName(a).localeCompare(this.employeeName(b)));
                }
            }),
        );

        this.subscriptions.add(
            this.partners$.subscribe((partners) => {
                if (!partners) {
                    this.logger.debug('Fetching partners');
                    this.store.dispatch(new FetchPartners());
                } else {
                    this.allPartners = partners;
                    if (this.project?.partnerUuid) this.partner = partners.find((partner) => partner.uuid === this.project.partnerUuid);
                    if (this.isUpdate || this.fromDeal) this.setFormValues();
                    this.isLoaded = true;
                }
            }),
        );

        this.subscriptions.add(
            this.companyUuid$.subscribe((uuid) => {
                this.companyUuid = uuid;
                this.logger.debug('Fetching resumes');
                this.store.dispatch(new FetchResumes());
            }),
        );
    }

    private getType(type: string): number {
        switch (type) {
            case 'CUSTOM':
                return 0;
            case 'WEBSITE':
                return 1;
            case 'CONSULTANCY':
                return 2;
            case 'HOSTING':
                return 4;
            case 'INTERN':
                return 5;
            default:
                return 3;
        }
    }
}
